import React from 'react';
import { Alert, Button, Box, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import * as Sentry from '@sentry/react';

class ChatErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console and Sentry
        console.error('Chat Error Boundary caught an error:', error, errorInfo);
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Log to Sentry
        Sentry.captureException(error, {
            tags: { component: 'chat-error-boundary' },
            extra: errorInfo
        });
    }

    handleReload = () => {
        // Clear error state to retry rendering
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null 
        });
    };

    handlePageRefresh = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ 
                    p: 3, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center'
                }}>
                    <Alert 
                        severity="error" 
                        sx={{ mb: 2, width: '100%', maxWidth: 500 }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Chat Error
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Something went wrong with the chat interface. This error has been logged.
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleReload}
                            >
                                Try Again
                            </Button>
                            
                            <Button
                                variant="contained"
                                size="small"
                                onClick={this.handlePageRefresh}
                            >
                                Refresh Page
                            </Button>
                        </Box>
                    </Alert>

                    {/* Show error details in development */}
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            bgcolor: '#f5f5f5', 
                            borderRadius: 1,
                            width: '100%',
                            maxWidth: 600,
                            maxHeight: 200,
                            overflow: 'auto'
                        }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                <strong>Error:</strong> {this.state.error.toString()}
                                <br />
                                <strong>Stack:</strong>
                                <pre style={{ fontSize: '0.75rem', margin: '8px 0' }}>
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        // If no error, render children normally
        return this.props.children;
    }
}

export default ChatErrorBoundary; 